import AbstractProvidedClass from "../shared/AbstractProvidedClass";
import EventTypes from "../shared/EventTypes";
import { TIP_BAR_HEIGHT } from "../shared/constants";

export default class Timeline extends AbstractProvidedClass {
    currentDate = null;
    minDate = null;
    maxDate = null;
    collapsed = true;

    constructor(provider) {
        super(provider);

        this.buildDOM();

        this.on(EventTypes.DATA_LOADED, () => this.init());
        this.on(EventTypes.UPDATE_ELEMENT_DATE, () => this.recalculateRange());
    }

    buildDOM() {
        let parent = document.getElementById('p5canvas');

        // Tab (always visible)
        let curveW = 18;
        let curveH = 12;
        this.tab = document.createElement('div');
        this.tab.className = 'planner-timeline-tab planner-ui-overlay';
        this.tab.style.position = 'absolute';
        this.tab.style.bottom = TIP_BAR_HEIGHT + 'px';
        this.tab.style.left = '50%';
        this.tab.style.transform = 'translateX(-50%)';
        this.tab.style.padding = '3px 8px';
        this.tab.style.backgroundColor = '#fff';
        this.tab.style.border = 'none';
        this.tab.style.borderRadius = `${curveH}px ${curveH}px 0 0`;
        this.tab.style.cursor = 'pointer';
        this.tab.style.fontSize = '11px';
        this.tab.style.color = '#555';
        this.tab.style.userSelect = 'none';
        this.tab.style.zIndex = '5';
        this.tab.style.overflow = 'visible';
        this.tab.style.boxShadow = '0 -1px 3px rgba(0,0,0,0.1)';
        this.tab.addEventListener('click', () => this.toggle());
        parent.appendChild(this.tab);

        // Text label (separate element so curves aren't destroyed)
        this.tabLabel = document.createElement('span');
        this.tabLabel.textContent = '▴ Timeline';
        this.tab.appendChild(this.tabLabel);

        // Left S-curve ear
        let leftCurve = document.createElement('div');
        leftCurve.style.position = 'absolute';
        leftCurve.style.bottom = '0';
        leftCurve.style.left = -curveW + 'px';
        leftCurve.style.width = curveW + 'px';
        leftCurve.style.height = curveH + 'px';
        leftCurve.style.background = `radial-gradient(ellipse ${curveW}px ${curveH}px at 0 0, transparent 99%, #fff 100%)`;
        this.tab.appendChild(leftCurve);

        // Right S-curve ear
        let rightCurve = document.createElement('div');
        rightCurve.style.position = 'absolute';
        rightCurve.style.bottom = '0';
        rightCurve.style.right = -curveW + 'px';
        rightCurve.style.width = curveW + 'px';
        rightCurve.style.height = curveH + 'px';
        rightCurve.style.background = `radial-gradient(ellipse ${curveW}px ${curveH}px at 100% 0, transparent 99%, #fff 100%)`;
        this.tab.appendChild(rightCurve);

        // Bar (collapsible)
        this.bar = document.createElement('div');
        this.bar.className = 'planner-timeline-bar planner-ui-overlay';
        this.bar.style.position = 'absolute';
        this.bar.style.bottom = TIP_BAR_HEIGHT + 'px';
        this.bar.style.left = '0';
        this.bar.style.right = '0';
        this.bar.style.backgroundColor = '#fff';
        this.bar.style.borderTop = '1px solid #ccc';
        this.bar.style.display = 'none';
        this.bar.style.alignItems = 'center';
        this.bar.style.padding = '4px 12px';
        this.bar.style.gap = '10px';
        this.bar.style.zIndex = '4';
        parent.appendChild(this.bar);

        // Min label
        this.minLabel = document.createElement('span');
        this.minLabel.style.fontSize = '10px';
        this.minLabel.style.color = '#888';
        this.minLabel.style.whiteSpace = 'nowrap';
        this.bar.appendChild(this.minLabel);

        // Custom slider
        this.sliderContainer = document.createElement('div');
        this.sliderContainer.style.flex = '1';
        this.sliderContainer.style.position = 'relative';
        this.sliderContainer.style.height = '28px';
        this.sliderContainer.style.cursor = 'pointer';
        this.sliderContainer.style.userSelect = 'none';
        this.sliderContainer.style.touchAction = 'none';
        this.bar.appendChild(this.sliderContainer);

        // Track
        this.track = document.createElement('div');
        this.track.style.position = 'absolute';
        this.track.style.top = '50%';
        this.track.style.left = '0';
        this.track.style.right = '0';
        this.track.style.height = '4px';
        this.track.style.transform = 'translateY(-50%)';
        this.track.style.backgroundColor = '#ddd';
        this.track.style.borderRadius = '2px';
        this.sliderContainer.appendChild(this.track);

        // Filled portion of track
        this.trackFill = document.createElement('div');
        this.trackFill.style.position = 'absolute';
        this.trackFill.style.top = '0';
        this.trackFill.style.left = '0';
        this.trackFill.style.height = '100%';
        this.trackFill.style.backgroundColor = '#087332';
        this.trackFill.style.borderRadius = '2px';
        this.track.appendChild(this.trackFill);

        // Thumb with date label
        this.thumb = document.createElement('div');
        this.thumb.style.position = 'absolute';
        this.thumb.style.top = '50%';
        this.thumb.style.transform = 'translate(-50%, -50%)';
        this.thumb.style.backgroundColor = '#087332';
        this.thumb.style.color = '#fff';
        this.thumb.style.fontSize = '10px';
        this.thumb.style.fontWeight = 'bold';
        this.thumb.style.padding = '3px 8px';
        this.thumb.style.borderRadius = '10px';
        this.thumb.style.whiteSpace = 'nowrap';
        this.thumb.style.cursor = 'grab';
        this.thumb.style.userSelect = 'none';
        this.thumb.style.zIndex = '2';
        this.sliderContainer.appendChild(this.thumb);

        // Today marker
        this.todayMarker = document.createElement('div');
        this.todayMarker.style.position = 'absolute';
        this.todayMarker.style.top = '50%';
        this.todayMarker.style.transform = 'translate(-50%, -50%)';
        this.todayMarker.style.cursor = 'pointer';
        this.todayMarker.style.lineHeight = '0';
        this.todayMarker.style.width = '19px';
        this.todayMarker.style.height = '19px';
        this.todayMarker.style.borderRadius = '50%';
        this.todayMarker.style.backgroundColor = '#fff';
        this.todayMarker.style.display = 'flex';
        this.todayMarker.style.alignItems = 'center';
        this.todayMarker.style.justifyContent = 'center';
        this.todayMarker.style.zIndex = '1';
        this.todayMarker.title = 'Jump to today';
        this.todayMarker.addEventListener('click', (e) => {
            e.stopPropagation();
            this.snapToToday();
        });
        this.sliderContainer.appendChild(this.todayMarker);

        let todayIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        todayIcon.setAttribute('width', '18');
        todayIcon.setAttribute('height', '18');
        todayIcon.style.fill = '#087332';
        todayIcon.style.opacity = '1';
        let use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
        use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '/icons/__remixicon.sprite.svg?v=491#time-fill');
        todayIcon.appendChild(use);
        this.todayMarker.appendChild(todayIcon);

        this.todayT = null;
        this.sliderValue = 1000;
        this.setupSliderDrag();

        // Max label
        this.maxLabel = document.createElement('span');
        this.maxLabel.style.fontSize = '10px';
        this.maxLabel.style.color = '#888';
        this.maxLabel.style.whiteSpace = 'nowrap';
        this.bar.appendChild(this.maxLabel);
    }

    get barHeight() {
        return this.collapsed ? 0 : this.bar.offsetHeight;
    }

    toggle() {
        this.collapsed = !this.collapsed;
        if (this.collapsed) {
            this.bar.style.display = 'none';
            this.tabLabel.textContent = '▴ Timeline';
            this.tab.style.bottom = TIP_BAR_HEIGHT + 'px';
        } else {
            this.bar.style.display = 'flex';
            this.tabLabel.textContent = '▾ Timeline';
            this.tab.style.bottom = (TIP_BAR_HEIGHT + this.bar.offsetHeight) + 'px';
            this.positionThumb();
        }
        this.emit(EventTypes.TIMELINE_TOGGLED, this.barHeight);
    }

    init() {
        this.recalculateRange();
        let now = new Date();
        this.currentDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        if (this.sliderMin && this.currentDate < this.sliderMin) this.currentDate = this.sliderMin;
        if (this.sliderMax && this.currentDate > this.sliderMax) this.currentDate = this.sliderMax;
        this.updateDisplay();
    }

    recalculateRange() {
        let min = null;
        let max = null;
        this.elements.collection.forEach(el => {
            if (el.addedDate) {
                min = !min || el.addedDate < min ? el.addedDate : min;
                max = !max || el.addedDate > max ? el.addedDate : max;
            }
            if (el.removedDate) {
                min = !min || el.removedDate < min ? el.removedDate : min;
                max = !max || el.removedDate > max ? el.removedDate : max;
            }
        });
        if (min && min.length === 4) min = min + '-01';
        if (max && max.length === 4) max = max + '-12';
        this.minDate = min;
        this.maxDate = max;
        this.sliderMin = min ? this.offsetMonth(min, -1) : null;
        this.sliderMax = max ? this.offsetMonth(max, 1) : null;
        this.updateDisplay();
    }

    get hasRange() {
        return this.minDate && this.maxDate && this.minDate !== this.maxDate;
    }

    setupSliderDrag() {
        const onPointerDown = (e) => {
            if (!this.hasRange) return;
            this.dragging = true;
            this.thumb.style.cursor = 'grabbing';
            this.updateSliderFromPointer(e);
            e.preventDefault();
        };

        const onPointerMove = (e) => {
            if (!this.dragging) return;
            this.updateSliderFromPointer(e);
        };

        const onPointerUp = () => {
            if (!this.dragging) return;
            this.dragging = false;
            this.thumb.style.cursor = 'grab';
        };

        this.sliderContainer.addEventListener('pointerdown', onPointerDown);
        document.addEventListener('pointermove', onPointerMove);
        document.addEventListener('pointerup', onPointerUp);
    }

    snapToToday() {
        if (!this.hasRange || this.todayT === null) return;
        this.sliderValue = Math.round(this.todayT * 1000);
        this.onSliderInput();
    }

    updateSliderFromPointer(e) {
        let rect = this.sliderContainer.getBoundingClientRect();
        let t = (e.clientX - rect.left) / rect.width;
        t = Math.max(0, Math.min(1, t));

        // Snap to today marker if within 12px
        if (this.todayT !== null) {
            let todayPx = this.todayT * rect.width;
            let pointerPx = t * rect.width;
            if (Math.abs(pointerPx - todayPx) < 12) {
                t = this.todayT;
            }
        }

        this.sliderValue = Math.round(t * 1000);
        this.onSliderInput();
    }

    onSliderInput() {
        if (!this.hasRange) return;
        let t = this.sliderValue / 1000;
        let min = this.dateToNumber(this.sliderMin);
        let max = this.dateToNumber(this.sliderMax);
        this.currentDate = this.numberToDate(min + t * (max - min));
        this.positionThumb();
        this.emit(EventTypes.TIMELINE_DATE_CHANGED, this.currentDate);
    }

    positionThumb() {
        let t = this.sliderValue / 1000;
        let pct = (t * 100) + '%';
        this.thumb.style.left = pct;
        this.thumb.textContent = this.formatDate(this.currentDate);
        this.trackFill.style.width = pct;
    }

    updateDisplay() {
        this.minLabel.textContent = this.formatDate(this.minDate);
        this.maxLabel.textContent = this.formatDate(this.maxDate);

        if (!this.hasRange) {
            this.thumb.textContent = this.minDate
                ? this.formatDate(this.minDate)
                : 'No dates set';
            this.thumb.style.left = '50%';
            this.thumb.style.cursor = 'default';
            this.thumb.style.opacity = '0.5';
            this.trackFill.style.width = '0';
            this.todayMarker.style.display = 'none';
        } else {
            this.thumb.style.cursor = 'grab';
            this.thumb.style.opacity = '1';
            let min = this.dateToNumber(this.sliderMin);
            let max = this.dateToNumber(this.sliderMax);
            let val = this.dateToNumber(this.currentDate);
            let t = (val - min) / (max - min);
            this.sliderValue = Math.round(t * 1000);
            this.positionThumb();

            // Position today marker
            let now = new Date();
            let todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
            let todayNum = this.dateToNumber(todayStr);
            if (todayNum >= min && todayNum <= max) {
                this.todayT = (todayNum - min) / (max - min);
                this.todayMarker.style.display = 'flex';
                this.todayMarker.style.left = (this.todayT * 100) + '%';
            } else {
                this.todayT = null;
                this.todayMarker.style.display = 'none';
            }
        }

        // Reposition tab if bar is visible
        if (!this.collapsed) {
            requestAnimationFrame(() => {
                this.tab.style.bottom = (TIP_BAR_HEIGHT + this.bar.offsetHeight) + 'px';
            });
        }
    }

    // Convert date string to a numeric value for interpolation
    dateToNumber(date) {
        if (!date) return 0;
        let parts = date.split('-').map(Number);
        let year = parts[0];
        let month = (parts[1] || 1) - 1;
        let day = (parts[2] || 1) - 1;
        return year + (month * 30 + day) / 365;
    }

    // Convert numeric value back to a date string (year-month granularity)
    numberToDate(num) {
        let year = Math.floor(num);
        let remainder = num - year;
        let dayOfYear = Math.round(remainder * 365);
        let month = Math.floor(dayOfYear / 30);
        if (month < 1) month = 0;
        if (month > 11) month = 11;
        return `${year}-${String(month + 1).padStart(2, '0')}`;
    }

    offsetMonth(date, offset) {
        let parts = date.split('-').map(Number);
        let year = parts[0];
        let month = (parts[1] || 1) + offset;
        if (month < 1) { year--; month = 12; }
        if (month > 12) { year++; month = 1; }
        return `${year}-${String(month).padStart(2, '0')}`;
    }

    formatDate(date) {
        if (!date) return '';
        let parts = date.split('-');
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        if (parts.length === 1) return parts[0];
        if (parts.length === 2) return `${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
        return `${parseInt(parts[2])} ${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    }

}
